// hooks/codes/mutations/useMergeCode.js
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { normalizeId } from '../../utils/idNormalization';
import { createCodeCacheUtils } from '../../utils/codeCacheUtils';
import { codeKeys } from '../queries';
import QualAPI from 'utils/QualAPI';
import { useOptimisticPromise } from 'hooks/optimistic/useOptimisticPromise';
import { removeCodeFromParent } from 'utils/codePositionUtils';

/**
 * Hook for merging one code into another with optimistic updates
 * 
 * This mutation:
 * 1. Takes a code to be merged (mergeCodeId) and a target code (mergeIntoCodeId)
 * 2. Optimistically removes the merged code from the UI
 * 3. Combines the names of both codes in the target code
 * 4. Updates the count to be the sum of both codes' counts
 * 5. Handles error reversal if the API call fails
 * 
 * @example
 * const { mutate: mergeCodes } = useMergeCode();
 * mergeCodes({ mergeCodeId: 123, mergeIntoCodeId: 456 });
 * 
 * @returns {Object} Mutation object with mutate function and state
 */
export default function useMergeCode() {
  const queryClient = useQueryClient();
  const cacheUtils = createCodeCacheUtils(queryClient);
  const { waitForPromise } = useOptimisticPromise();
  
  return useMutation({
    mutationFn: async ({ mergeCodeId, mergeIntoCodeId, trackingContext }) => {
      const resolvedMergeCodeId = await waitForPromise(mergeCodeId);
      const resolvedMergeIntoCodeId = await waitForPromise(mergeIntoCodeId);

      return QualAPI.mergeCode(resolvedMergeCodeId, resolvedMergeIntoCodeId, trackingContext)
        .then(response => response.data);
    },
    
    onMutate: async (variables) => {
      const { mergeCodeId, mergeIntoCodeId } = variables;
      const normalizedMergeCodeId = normalizeId(mergeCodeId);
      const normalizedMergeIntoCodeId = normalizeId(mergeIntoCodeId);
      
      // Get current codes to know their details
      const mergeCode = queryClient.getQueryData(codeKeys.detail(normalizedMergeCodeId));
      const mergeIntoCode = queryClient.getQueryData(codeKeys.detail(normalizedMergeIntoCodeId));
      
      if (!mergeCode || !mergeIntoCode) return {};
      
      // Ensure both codes are from the same project
      if (normalizeId(mergeCode.project_id) !== normalizeId(mergeIntoCode.project_id)) {
        console.error('Cannot merge codes from different projects');
        return {};
      }
      
      const projectId = normalizeId(mergeCode.project_id);
      const projectCodesKey = codeKeys.list(projectId);
      const projectCodes = queryClient.getQueryData(projectCodesKey);
      
      if (!projectCodes?.codes) return {};
      
      // Cancel queries
      await cacheUtils.cancelQueries(normalizedMergeCodeId);
      await cacheUtils.cancelQueries(normalizedMergeIntoCodeId);
      
      // Get the code positions
      const mergeCodePosition = mergeCode.position || 1;
      const mergeCodeParentId = mergeCode.parent_id || null;
      
      // Compute the combined name
      const combinedName = `${mergeIntoCode.name} | ${mergeCode.name}`;
      
      // Calculate the combined count
      const combinedCount = (mergeIntoCode.count || 0) + (mergeCode.count || 0);
      
      // 1. Remove the merged code from its parent (shift positions down)
      let updatedCodes = removeCodeFromParent(
        projectCodes.codes, 
        mergeCodeParentId, 
        mergeCodePosition
      );
      
      // 2. Filter out the merged code from the list
      updatedCodes = updatedCodes.filter(code => 
        normalizeId(code.id) !== normalizedMergeCodeId
      );
      
      // Update project codes cache
      queryClient.setQueryData(projectCodesKey, {
        ...projectCodes,
        codes: updatedCodes
      });
      
      // Update the target code with the combined name and count
      cacheUtils.updateCode(normalizedMergeIntoCodeId, {
        name: combinedName,
        count: combinedCount
      });

      // Update all excerpt codings to point to the merge target code
      cacheUtils.updateExcerptCodingsForMerge(
        normalizedMergeCodeId,
        normalizedMergeIntoCodeId
      );

      // Remove the merged code from the cache
      queryClient.removeQueries({ queryKey: codeKeys.detail(normalizedMergeCodeId) });
      
      return { 
        projectId,
        normalizedMergeCodeId,
        normalizedMergeIntoCodeId,
        previousMergeCodeName: mergeCode.name,
        previousMergeIntoCodeName: mergeIntoCode.name,
        previousMergeIntoCodeCount: mergeIntoCode.count
      };
    },
    
    onError: (_, __, context) => {
      if (context) {
        const { projectId } = context;
        
        // Invalidate the project codes list to refresh data
        queryClient.invalidateQueries({ queryKey: codeKeys.list(projectId) });
      }
    },
    
    onSettled: (_, __, ___, context) => {
      if (context) {
        const { projectId } = context;
        
        // Invalidate the project codes list to refresh data
        queryClient.invalidateQueries({ queryKey: codeKeys.list(projectId) });
      }
    }
  });
}
